import { useRef, useLayoutEffect } from 'react';
import type { FC, CSSProperties } from 'react';

import { useSize } from 'app/hooks/useSize';

interface Props {
  children: string;
  nbEndChars?: number;
  className?: string;
  style?: CSSProperties;
  withTitle?: boolean;
}

const textStyle: CSSProperties = {
  display: 'inline-block',
  whiteSpace: 'nowrap'
};

const truncateElementAtCenter = (children: string, element: HTMLElement, width: number): void => {
  let middle = Math.round(children.length / 2);

  let text = `${children.slice(0, middle)}...${children.slice(middle)}`;
  element.innerText = text;
  let textWidth = element.clientWidth;

  for (let i = 0; textWidth > width; i += 1) {
    let indexToRemove;
    if (i % 2 === 0) {
      indexToRemove = middle; // remove letter before ...
      middle -= 1;
    } else {
      indexToRemove = middle + 4; // remove letter after ...
    }
    text = `${text.slice(0, indexToRemove - 1)}${text.slice(indexToRemove)}`;
    element.innerText = text;
    textWidth = element.clientWidth;
  }
};

const truncateElementAtEnd = (
  children: string,
  nbChars: number,
  element: HTMLElement,
  width: number
) => {
  const endSliceLength = nbChars + 3; // 3 because we add ... in text
  let text = `${children.slice(0, -nbChars)}...${children.slice(-nbChars)}`;
  element.innerText = text;
  let textWidth = element.clientWidth;

  text = `${text.slice(0, -endSliceLength - 1)}${text.slice(-endSliceLength)}`;
  element.innerText = text;
  textWidth = element.clientWidth;

  while (textWidth > width) {
    text = `${text.slice(0, -endSliceLength - 1)}${text.slice(-endSliceLength)}`;
    element.innerText = text;
    textWidth = element.clientWidth;
  }
};

const EllipsisMiddle: FC<Props> = ({
  children,
  nbEndChars,
  className,
  style,
  withTitle = false
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { width } = useSize(ref);

  useLayoutEffect(() => {
    if (ref.current) {
      const textElement = ref.current.children[0] as HTMLSpanElement;

      if (width <= 0) {
        textElement.innerText = '';
      } else {
        textElement.innerText = children;
        const textWidth = textElement.clientWidth;

        if (textWidth > width) {
          if (nbEndChars === undefined) {
            truncateElementAtCenter(children, textElement, width);
          } else {
            truncateElementAtEnd(children, nbEndChars, textElement, width);
          }
        }
      }
    }
  }, [children, width]);

  return (
    <div ref={ref} className={className} style={style} title={withTitle ? children : undefined}>
      <span style={textStyle}>{children}</span>
    </div>
  );
};

EllipsisMiddle.displayName = 'EllipsisMiddle';

export default EllipsisMiddle;
